/**
 * Certificaciones y estado del período para el modo vista previa.
 *
 * A diferencia del resto de la vista previa, esto **guarda**: las altas, las
 * correcciones y los borrados quedan en memoria mientras dure la pestaña. Sin
 * eso no se puede probar lo único que importa de esta pantalla, que es que lo
 * cargado se conserve.
 *
 * Los conteos se **derivan de las filas**, nunca se escriben aparte: ya pasó
 * dos veces en el monitor que un resumen escrito a mano dijera una cosa y la
 * tabla otra.
 */
import {
  Certificacion,
  LiberacionProvision,
  LineaCertificacion,
  Provision
} from '../core/interfaces/certificacion.interface';
import { HOJAS_DEMO, nombreDeElementoEdt, numeroDeElementoEdt } from './preview-edt';

const hoy = new Date();
const iso = (d: Date) => d.toISOString().slice(0, 10);
const mesAtras = (n: number) => new Date(hoy.getFullYear(), hoy.getMonth() - n, 15);

/** El anteúltimo mes está cerrado: así se ve el aviso sin tener que cerrarlo */
const PERIODO_CERRADO = iso(mesAtras(2)).slice(0, 7);

let siguienteId = 1;
let siguienteNumero = 1;

function nueva(parcial: Partial<Certificacion> & { fecha_certificacion: string | null }): Certificacion {
  const lineas = parcial.lineas ?? [];
  const horas = lineas.reduce((t, l) => t + Number(l.cantidad ?? 0), 0);

  const armada: Certificacion = {
    id: siguienteId++,
    numero: 'CERT-' + String(siguienteNumero++).padStart(4, '0'),
    modalidad: 'consultor',
    servicio_desde: parcial.fecha_certificacion,
    servicio_hasta: parcial.fecha_certificacion,
    periodo: (parcial.fecha_certificacion ?? new Date().toISOString()).slice(0, 7),
    anticipada: false,
    persona_contacto: null,
    responsable_cliente: null,
    observaciones: null,
    hito: null,
    porcentaje: null,
    total_horas: '0',
    total_importe: null,
    moneda: 'USD',
    estado: 'borrador',
    editable: true,
    autor: 'Facu Aguirre',
    creada: new Date().toISOString(),
    ...parcial,
    lineas
  };

  /* Se derivan al final, después del spread: las horas salen de las líneas y
     que sea editable sale del estado. Aceptarlos de afuera es justo la puerta
     por la que un resumen dice una cosa y la tabla otra. */
  armada.total_horas = String(horas);
  armada.editable = armada.estado === 'borrador';

  return armada;
}

const filas: Certificacion[] = [
  nueva({
    fecha_certificacion: iso(mesAtras(1)),
    servicio_desde: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)),
    servicio_hasta: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0)),
    periodo: iso(mesAtras(1)).slice(0, 7),
    persona_contacto: 'Julián Álvarez',
    estado: 'enviado',
    lineas: [
      { nombre: 'Juan Pérez', cantidad: 160, observaciones: 'Sprint 1 y 2' },
      { nombre: 'María García', cantidad: 120, observaciones: 'Sprint 2' }
    ]
  }),
  nueva({
    fecha_certificacion: iso(mesAtras(1)),
    servicio_desde: iso(new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1)),
    servicio_hasta: iso(new Date(hoy.getFullYear(), hoy.getMonth(), 0)),
    periodo: iso(mesAtras(1)).slice(0, 7),
    persona_contacto: 'Emilce Maidana',
    lineas: [{ nombre: 'Carlos López', cantidad: 48, observaciones: 'Soporte' }]
  }),
  nueva({
    fecha_certificacion: iso(mesAtras(2)),
    periodo: PERIODO_CERRADO,
    persona_contacto: 'Julián Álvarez',
    estado: 'facturado',
    lineas: [{ nombre: 'Juan Pérez', cantidad: 152 }]
  })
];

function resumenDe(delPeriodo: Certificacion[]) {
  /* **Certificada es la que el cliente aprobó.** Sumaba cualquier estado, así
     que un borrador que nadie mandó entraba en «horas certificadas» y se
     contaba otra vez en la tarjeta de borradores de al lado. */
  const aprobadas = ['aprobado', 'enviado', 'facturado'];

  const sumar = (cuales: Certificacion[]) =>
    String(cuales.reduce((t, c) => t + Number(c.total_horas), 0));

  return {
    borrador: delPeriodo.filter((c) => c.estado === 'borrador').length,
    enviado: delPeriodo.filter((c) => c.estado === 'enviado').length,
    facturado: delPeriodo.filter((c) => c.estado === 'facturado').length,
    horas: sumar(delPeriodo.filter((c) => aprobadas.includes(c.estado))),
    horas_sin_aprobar: sumar(delPeriodo.filter((c) => c.estado === 'borrador')),
    anticipadas: delPeriodo.filter((c) => c.anticipada).length
  };
}

function desdeElCuerpo(cuerpo: any, base?: Certificacion): Certificacion {
  const lineas: LineaCertificacion[] = (cuerpo?.lineas ?? []).map((l: any) => ({
    nombre: l.nombre ?? null,
    cantidad: Number(l.cantidad ?? 0),
    observaciones: l.observaciones ?? null
  }));

  const hasta = String(cuerpo?.servicio_hasta ?? '');

  // Un borrador puede llegar sin fechas, como en el backend (2/11/2026)
  const datos: Partial<Certificacion> & { fecha_certificacion: string | null } = {
    fecha_certificacion: cuerpo?.fecha_certificacion ?? null,
    servicio_desde: cuerpo?.servicio_desde ?? null,
    servicio_hasta: hasta || null,
    periodo: String(cuerpo?.servicio_desde ?? cuerpo?.fecha_certificacion ?? iso(hoy)).slice(0, 7),
    // La misma regla que el backend: si el servicio termina después de hoy
    anticipada: !!hasta && hasta > iso(hoy),
    persona_contacto: cuerpo?.persona_contacto ?? null,
    responsable_cliente: cuerpo?.responsable_cliente ?? null,
    observaciones: cuerpo?.observaciones ?? null,
    lineas
  };

  if (!base) return nueva(datos);

  const horas = lineas.reduce((t, l) => t + Number(l.cantidad ?? 0), 0);
  return { ...base, ...datos, total_horas: String(horas) };
}

/* ----- Provisiones ------------------------------------------------------
   La otra mitad del modelo: acá el mes **es** el dato. Una provisión se carga
   para el mes en curso o los que vienen, nunca uno que ya pasó. */

/* Las que se ofrecen al provisionar. **Son componentes de costo de ARMUS**,
   los mismos que lleva cada línea del EDT: antes eran tres nombres inventados
   —«Personal», «Marketing», «Tecnología»— que no cruzaban con nada, y el demo
   enseñaba un modelo que no es el que corre. */
const CATEGORIAS_DEMO = [
  'Licencias y software',
  'Mano de obra directa',
  'Subcontratos',
  'Viáticos y traslados'
];

const mesComoTexto = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const MES_ACTUAL = mesComoTexto(hoy);

let siguienteIdProvision = 1;
let siguienteIdLiberacion = 1;

/* El envío a ARMUS viene apagado: los movimientos se encolan igual */
let pendientesArmus = 0;

function provision(
  periodo: string, categoria: string, edt: string, desc: string, monto: string,
  liberaciones: LiberacionProvision[] = [], edtItemId: number | null = null
): Provision {
  const [anio, mes] = periodo.split('-');
  const liberado = liberaciones.reduce((t, l) => t + Number(l.monto), 0);
  const saldo = Number(monto) - liberado;

  return {
    id: siguienteIdProvision++,
    periodo,
    anio: Number(anio),
    mes: Number(mes),
    categoria,
    elemento_edt: edt,
    edt_item_id: edtItemId,
    edt_numero: numeroDeElementoEdt(edtItemId),
    descripcion: desc,
    monto,
    liberado: String(liberado),
    saldo: String(saldo),
    liberada: saldo <= 0,
    moneda: 'USD',
    autor: 'Facu Aguirre',
    creada: new Date().toISOString(),
    /* Se derivan, igual que en el backend. Y son cosas distintas: una
       provisión de un mes que ya pasó no se corrige, pero **sí se libera** —es
       justo para lo que existe la liberación—. */
    editable: periodo >= MES_ACTUAL && periodo !== PERIODO_CERRADO && liberaciones.length === 0,
    liberable: saldo > 0,
    liberaciones
  };
}

/* Imputadas a elementos del árbol de verdad: el «código» del elemento es su
   número (1.1, 1.2), no uno inventado. */
const provisiones: Provision[] = [
  provision(MES_ACTUAL, 'Mano de obra directa', nombreDeElementoEdt(HOJAS_DEMO.analisis) ?? '',
    'Salarios del equipo de desarrollo', '45000', [], HOJAS_DEMO.analisis),
  provision(mesComoTexto(new Date(hoy.getFullYear(), hoy.getMonth() + 1, 1)),
    'Licencias y software', nombreDeElementoEdt(HOJAS_DEMO.otros) ?? '',
    'Licencias de software y herramientas', '12500', [], HOJAS_DEMO.otros),
  // Una de un mes que ya pasó: se ve, no se toca
  provision(mesComoTexto(mesAtras(1)), 'Viáticos y traslados', nombreDeElementoEdt(HOJAS_DEMO.viaticos) ?? '',
    'Campañas del cierre anterior', '8300', [], HOJAS_DEMO.viaticos)
];

function respuestaProvisiones(metodo: string, ruta: string, params: URLSearchParams, cuerpo: any): unknown | null {
  const base = ruta.match(/\/api\/project\/([^/]+)\/provisiones\/?$/);
  const conId = ruta.match(/\/api\/project\/([^/]+)\/provisiones\/(\d+)\/?$/);
  const liberar = ruta.match(/\/api\/project\/([^/]+)\/provisiones\/(\d+)\/liberar\/?$/);
  const quitar = ruta.match(/\/api\/project\/([^/]+)\/provisiones\/(\d+)\/liberaciones\/(\d+)\/?$/);

  // Las cuatro formas van juntas: sin las dos últimas, liberar salía por acá
  const facturas = /\/api\/project\/([^/]+)\/provisiones\/facturas-costo\/?$/.test(ruta);

  if (!base && !conId && !liberar && !quitar && !facturas) return null;

  /* Las facturas de costo. Vienen vacías a propósito: **ARMUS todavía no las
     expone**, y la vista previa tiene que mostrar lo que de verdad pasa —el
     modal explicando que falta el endpoint y ofreciendo liberar sin factura—
     y no una lista inventada que después no va a estar. */
  if (/\/api\/project\/([^/]+)\/provisiones\/facturas-costo\/?$/.test(ruta)) {
    return { success: true, data: [], replicacion_activa: false };
  }

  if (base && metodo === 'GET') {
    const categoria = params.get('categoria');
    const data = provisiones.filter((p) => !categoria || categoria === 'todas' || p.categoria === categoria);

    return {
      success: true,
      data,
      total: data.length,
      categorias: CATEGORIAS_DEMO,
      resumen: {
        cantidad: data.length,
        monto: String(data.reduce((t, p) => t + Number(p.monto), 0)),
        saldo: String(data.reduce((t, p) => t + Number(p.saldo), 0)),
        moneda: 'USD'
      },
      armus: { activo: false, pendientes: pendientesArmus }
    };
  }

  if (base && metodo === 'POST') {
    const periodo = String(cuerpo?.periodo ?? MES_ACTUAL);

    if (periodo < MES_ACTUAL) {
      return { success: false, message: `No se puede provisionar ${periodo}: ese mes ya pasó.` };
    }

    const idEdt = cuerpo?.edt_item_id ? Number(cuerpo.edt_item_id) : null;
    const nombreEdt = nombreDeElementoEdt(idEdt) ?? (cuerpo?.elemento_edt ?? '');

    const p = provision(periodo, String(cuerpo?.categoria ?? ''), nombreEdt,
      cuerpo?.descripcion ?? '', String(cuerpo?.monto ?? 0), [],
      nombreDeElementoEdt(idEdt) ? idEdt : null);
    provisiones.push(p);
    pendientesArmus++;
    return {
      success: true,
      message: 'Provisión guardada. Queda en cola para ARMUS: el envío está apagado.',
      data: p
    };
  }

  if (liberar && metodo === 'POST') {
    const i = provisiones.findIndex((p) => p.id === Number(liberar[2]));
    if (i === -1) return { success: false, message: 'No existe esa provisión.' };

    const p = provisiones[i];
    const monto = Number(cuerpo?.monto ?? p.saldo);

    if (monto > Number(p.saldo) + 0.0001) {
      return { success: false, message: `No se puede liberar ${monto}: quedan ${p.saldo}.` };
    }

    // Igual que el backend: sin decir contra qué gasto, no se libera
    if (!String(cuerpo?.referencia ?? '').trim()) {
      return { success: false, message: 'Falta la factura de costo contra la que se libera.' };
    }

    const nueva: LiberacionProvision = {
      id: siguienteIdLiberacion++,
      periodo: String(cuerpo?.periodo ?? MES_ACTUAL),
      monto: String(monto),
      referencia: cuerpo?.referencia ?? null,
      observaciones: cuerpo?.observaciones ?? null
    };

    const rearmada = provision(p.periodo, p.categoria, p.elemento_edt ?? '', p.descripcion ?? '',
      p.monto, [...p.liberaciones, nueva]);
    provisiones[i] = { ...rearmada, id: p.id };
    siguienteIdProvision--;
    pendientesArmus++;

    return { success: true, message: 'Provisión liberada.', data: provisiones[i] };
  }

  if (quitar && metodo === 'DELETE') {
    const i = provisiones.findIndex((p) => p.id === Number(quitar[2]));
    if (i === -1) return { success: false, message: 'No existe esa provisión.' };

    const p = provisiones[i];
    const quedan = p.liberaciones.filter((l) => l.id !== Number(quitar[3]));
    const rearmada = provision(p.periodo, p.categoria, p.elemento_edt ?? '', p.descripcion ?? '',
      p.monto, quedan);
    provisiones[i] = { ...rearmada, id: p.id };
    siguienteIdProvision--;
    pendientesArmus++;

    return { success: true, message: 'Liberación quitada.', data: provisiones[i] };
  }

  if (conId) {
    const i = provisiones.findIndex((p) => p.id === Number(conId[2]));
    if (i === -1) return { success: false, message: 'No existe esa provisión.' };

    if (metodo === 'PUT') {
      const periodo = String(cuerpo?.periodo ?? provisiones[i].periodo);

      if (periodo < MES_ACTUAL) {
        return { success: false, message: `No se puede provisionar ${periodo}: ese mes ya pasó.` };
      }

      const idEdt = cuerpo?.edt_item_id ? Number(cuerpo.edt_item_id) : null;
      const nombreEdt = nombreDeElementoEdt(idEdt) ?? (cuerpo?.elemento_edt ?? '');

      const nueva = provision(periodo, String(cuerpo?.categoria ?? ''), nombreEdt,
        cuerpo?.descripcion ?? '', String(cuerpo?.monto ?? 0), [],
        nombreDeElementoEdt(idEdt) ? idEdt : null);
      provisiones[i] = { ...nueva, id: provisiones[i].id };
      siguienteIdProvision--;
      return { success: true, data: provisiones[i] };
    }

    if (metodo === 'DELETE') {
      provisiones.splice(i, 1);
      return { success: true, message: 'Provisión eliminada.' };
    }
  }

  return null;
}

export function respuestaCertificaciones(
  metodo: string,
  ruta: string,
  params: URLSearchParams,
  cuerpo: unknown
): unknown | null {
  const provs = respuestaProvisiones(metodo, ruta, params, cuerpo);
  if (provs !== null) return provs;

  /* La lista de la cabecera (28/10/2026): enero a septiembre de 2026
     cerrados, como en PROD, y el mes en curso abierto. */
  if (metodo === 'GET' && /\/api\/periodos\/?$/.test(ruta)) {
    const hoy = new Date();
    const data = Array.from({ length: Number(params.get('meses') ?? 24) }, (_, i) => {
      const d = new Date(hoy.getFullYear(), hoy.getMonth() - i, 1);
      const periodo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      return { periodo, anio: d.getFullYear(), mes: d.getMonth() + 1,
        estado: periodo >= '2026-01' && periodo <= '2026-09' ? 'cerrado' : 'abierto' };
    });
    return { success: true, data };
  }

  const periodoPedido = ruta.match(/\/api\/periodos\/(\d{4}-\d{2})\/?$/);
  if (periodoPedido) {
    const p = periodoPedido[1];
    return {
      success: true,
      data: {
        periodo: p,
        estado: p === PERIODO_CERRADO ? 'cerrado' : 'abierto',
        cerrado_at: p === PERIODO_CERRADO ? new Date().toISOString() : null,
        cerrado_por: p === PERIODO_CERRADO ? 'Control de Gestión' : null,
        historial: []
      }
    };
  }

  /* Las posiciones de las órdenes, en la moneda del CECO. Una en pesos y
     otra de una orden en dólares, que es el caso del aviso con las dos
     monedas (Facu, 14/10/2026). */
  if (metodo === 'GET' && /\/api\/project\/[^/]+\/posiciones\/?$/.test(ruta)) {
    return {
      success: true,
      moneda: 'ARS',
      data: [
        {
          id: 901, orden: 'S00099', numero: 1, descripcion: 'Implementación', producto: 'Servicio Implementacion - BS',
          monto_neto: 4800000, disponible: 2400000, sin_disponible: false,
          moneda_distinta: false, moneda_orden: 'ARS', disponible_moneda_orden: 2400000, cotizacion_al: '2026-10-14',
          porcentaje_disponible: 50,
          precio_unitario: 25000
        },
        {
          id: 902, orden: 'S00006', numero: 1, descripcion: 'Consultoría', producto: 'Horas de Consultoria - PF',
          monto_neto: 154500, disponible: 154500, sin_disponible: false,
          moneda_distinta: true, moneda_orden: 'USD', disponible_moneda_orden: 100, cotizacion_al: '2026-10-14',
          porcentaje_disponible: 100,
          precio_unitario: 18500
        }
      ]
    };
  }

  /* Lo que hay para certificar: horas aprobadas sin declarar, con lo que
     viene arrastrado de meses anteriores. En el demo sale de dos personas
     fijas, una con remanente, que es el caso que hay que poder ver. */
  if (metodo === 'GET' && /\/certificaciones\/disponible$/.test(ruta)) {
    const personas = [
      {
        employee_id: 39, nombre: 'Eliana Gabriela Alvarez', email: 'ealvarez@vippinn.com',
        horas_del_periodo: 96, remanente_anterior: 0, disponible: 96,
        aprobadas_acumuladas: 96, ya_certificadas: 0
      },
      {
        /* Con remanente: doce horas de un mes anterior que nadie declaró. */
        employee_id: 47, nombre: 'Diego Sosa', email: 'dsosa@vippinn.com',
        horas_del_periodo: 24, remanente_anterior: 12, disponible: 36,
        aprobadas_acumuladas: 288, ya_certificadas: 252
      }
    ];

    return {
      success: true,
      periodo: params.get('periodo') ?? '',
      personas,
      total_disponible: personas.reduce((t, p) => t + p.disponible, 0),
      total_del_periodo: personas.reduce((t, p) => t + p.horas_del_periodo, 0),
      total_remanente: personas.reduce((t, p) => t + p.remanente_anterior, 0)
    };
  }

  const base = ruta.match(/\/api\/project\/([^/]+)\/certificaciones\/?$/);
  const conId = ruta.match(/\/api\/project\/([^/]+)\/certificaciones\/(\d+)(\/enviar)?\/?$/);

  if (!base && !conId) return null;

  const cerrado = (periodo: string) => periodo === PERIODO_CERRADO;

  if (base && metodo === 'GET') {
    const periodo = params.get('periodo');
    const estado = params.get('estado');

    // Un borrador sin fecha aparece en cualquier mes, como en el backend
    let data = filas.filter((c) => !periodo || c.periodo === periodo || !c.fecha_certificacion);
    const delPeriodo = [...data];

    // Uno o varios, separados por coma, como el backend (2/11/2026)
    const estados = (estado ?? '').split(',').filter((e) => e && e !== 'todos');
    if (estados.length) data = data.filter((c) => estados.includes(c.estado));

    return {
      success: true,
      data: [...data].sort((a, b) => (b.fecha_certificacion ?? "").localeCompare(a.fecha_certificacion ?? "")),
      total: data.length,
      pagina: 1,
      paginas: 1,
      resumen: resumenDe(delPeriodo)
    };
  }

  if (base && metodo === 'POST') {
    const c = desdeElCuerpo(cuerpo);

    if (cerrado(c.periodo)) {
      return { success: false, message: `El período ${c.periodo} está cerrado.` };
    }

    filas.push(c);
    return { success: true, message: 'Certificación guardada.', data: c };
  }

  if (conId) {
    const id = Number(conId[2]);
    const i = filas.findIndex((c) => c.id === id);

    if (i === -1) return { success: false, message: 'No existe esa certificación.' };

    if (conId[3] && metodo === 'POST') {
      filas[i] = { ...filas[i], estado: 'enviado', editable: false };
      return { success: true, data: filas[i] };
    }

    if (metodo === 'PUT') {
      filas[i] = desdeElCuerpo(cuerpo, filas[i]);
      return { success: true, data: filas[i] };
    }

    if (metodo === 'DELETE') {
      filas.splice(i, 1);
      return { success: true, message: 'Borrador eliminado.' };
    }
  }

  return null;
}
